// Define individual permissions as an enum
export enum Permission {
  CAN_VIEW = "CAN_VIEW",
  CAN_EDIT_REPORTS = "CAN_EDIT_REPORTS",
  CAN_MANAGE_ACCESS = "CAN_MANAGE_ACCESS",
  CAN_CREATE_REPORTS = "CAN_CREATE_REPORTS",
  CAN_DOWNLOAD_FILE = "CAN_DOWNLOAD_FILE",
  CAN_UPLOAD_FILE = "CAN_UPLOAD_FILE",
  CAN_VIEW_FILE = "CAN_VIEW_FILE",
  CAN_DELETE_FILE = "CAN_DELETE_FILE",
  CAN_EDIT_FILE = "CAN_EDIT_FILE",
  CAN_VIEW_REPORTS_FILES = "CAN_VIEW_REPORTS_FILES",
  CAN_LEAVE_COMMENTS = "CAN_LEAVE_COMMENTS",
}

// Define project roles with corresponding permissions
export const ProjectRoles = {
  FULL_ACCESS: [
    Permission.CAN_VIEW,
    Permission.CAN_EDIT_REPORTS,
    Permission.CAN_CREATE_REPORTS,
    Permission.CAN_DOWNLOAD_FILE,
    Permission.CAN_UPLOAD_FILE,
    Permission.CAN_VIEW_FILE,
    Permission.CAN_DELETE_FILE,
    Permission.CAN_EDIT_FILE,
    Permission.CAN_VIEW_REPORTS_FILES,
    Permission.CAN_LEAVE_COMMENTS,
  ],
  CONTRIBUTOR: [
    Permission.CAN_VIEW,
    Permission.CAN_EDIT_REPORTS,
    Permission.CAN_LEAVE_COMMENTS,
    Permission.CAN_VIEW_REPORTS_FILES,
    Permission.CAN_UPLOAD_FILE,
    Permission.CAN_VIEW_FILE,
    Permission.CAN_EDIT_FILE,
  ],
  REPORT_READER: [
    Permission.CAN_VIEW,
    Permission.CAN_VIEW_REPORTS_FILES,
    Permission.CAN_LEAVE_COMMENTS,
  ],
} as const;

export type ProjectRole = keyof typeof ProjectRoles;

// Define report roles with corresponding permissions
export const ReportRoles = {
  EDITOR: [
    Permission.CAN_VIEW,
    Permission.CAN_EDIT_REPORTS,
    Permission.CAN_VIEW_REPORTS_FILES,
    Permission.CAN_LEAVE_COMMENTS,
  ],
  READ_AND_COMMENT: [Permission.CAN_VIEW, Permission.CAN_LEAVE_COMMENTS],
  READONLY: [Permission.CAN_VIEW],
} as const;

export type ReportRole = keyof typeof ReportRoles;

// Function to check if a user has a permission in a given project
export function hasPermission(
  userPermissions: Permission[],
  permission: Permission
): boolean {
  return userPermissions.includes(permission);
}

export function hasProjectRole(
  userPermissions: string[],
  role: ProjectRole
): boolean {
  const requiredPermissions = ProjectRoles[role];
  return requiredPermissions.every((perm) => userPermissions.includes(perm));
}
